<template>
  <canvas ref="canvasRef" class="w-full h-full" />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'

interface Props {
  beamCount?: number
  colorScheme?: 'rainbow' | 'purple' | 'blue'
  speedFactor?: number
}

const props = withDefaults(defineProps<Props>(), {
  beamCount: 80,
  colorScheme: 'rainbow',
  speedFactor: 1
})

const canvasRef = ref<HTMLCanvasElement | null>(null)
let animationId: number | null = null

interface Beam {
  x: number
  y: number
  length: number
  speed: number
  angle: number
  color: string
  opacity: number
  width: number
}

function getColor(scheme: string): string {
  switch (scheme) {
    case 'rainbow': {
      const hue = Math.random() * 360
      return `hsl(${hue}, 80%, 65%)`
    }
    case 'purple': {
      const hue = 250 + Math.random() * 40
      return `hsl(${hue}, 75%, 65%)`
    }
    case 'blue': {
      const hue = 200 + Math.random() * 40
      return `hsl(${hue}, 75%, 65%)`
    }
    default:
      return `hsl(${Math.random() * 360}, 80%, 65%)`
  }
}

function createBeam(width: number, height: number): Beam {
  const centerX = width / 2
  const centerY = height / 2
  const angle = Math.random() * Math.PI * 2

  return {
    x: centerX,
    y: centerY,
    length: Math.random() * 80 + 20,
    speed: (Math.random() * 3 + 1) * props.speedFactor,
    angle,
    color: getColor(props.colorScheme),
    opacity: Math.random() * 0.4 + 0.1,
    width: Math.random() * 1.5 + 0.5
  }
}

function startAnimation() {
  const canvas = canvasRef.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  let width = 0
  let height = 0
  let beams: Beam[] = []

  function resize() {
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    width = rect.width
    height = rect.height
    canvas.width = width * dpr
    canvas.height = height * dpr
    ctx!.scale(dpr, dpr)
  }

  function initBeams() {
    beams = []
    for (let i = 0; i < props.beamCount; i++) {
      const beam = createBeam(width, height)
      // Scatter initial positions outward so they don't all start from center
      const dist = Math.random() * Math.max(width, height) * 0.7
      beam.x = width / 2 + Math.cos(beam.angle) * dist
      beam.y = height / 2 + Math.sin(beam.angle) * dist
    }
    for (let i = 0; i < props.beamCount; i++) {
      beams.push(createBeam(width, height))
      // Scatter initial positions
      const dist = Math.random() * Math.max(width, height) * 0.7
      beams[i].x = width / 2 + Math.cos(beams[i].angle) * dist
      beams[i].y = height / 2 + Math.sin(beams[i].angle) * dist
    }
  }

  resize()
  initBeams()

  const resizeObserver = new ResizeObserver(() => {
    resize()
    initBeams()
  })
  resizeObserver.observe(canvas)

  function draw() {
    if (!ctx) return

    ctx.clearRect(0, 0, width, height)

    for (const beam of beams) {
      // Move beam outward from center
      beam.x += Math.cos(beam.angle) * beam.speed
      beam.y += Math.sin(beam.angle) * beam.speed

      // Calculate distance from center for fading
      const dx = beam.x - width / 2
      const dy = beam.y - height / 2
      const dist = Math.sqrt(dx * dx + dy * dy)
      const maxDist = Math.max(width, height) * 0.8

      // Reset if too far from view
      if (dist > maxDist) {
        beam.x = width / 2
        beam.y = height / 2
        beam.angle = Math.random() * Math.PI * 2
        beam.speed = (Math.random() * 3 + 1) * props.speedFactor
        beam.color = getColor(props.colorScheme)
        beam.opacity = Math.random() * 0.4 + 0.1
        beam.length = Math.random() * 80 + 20
      }

      // Fade in as beam moves away from center
      const fadeIn = Math.min(dist / 100, 1)
      const alpha = beam.opacity * fadeIn

      // Draw beam as a line from current position extending outward
      const tailX = beam.x - Math.cos(beam.angle) * beam.length * (dist / maxDist + 0.2)
      const tailY = beam.y - Math.sin(beam.angle) * beam.length * (dist / maxDist + 0.2)

      ctx.beginPath()
      ctx.moveTo(tailX, tailY)
      ctx.lineTo(beam.x, beam.y)
      ctx.strokeStyle = beam.color
      ctx.globalAlpha = alpha
      ctx.lineWidth = beam.width
      ctx.lineCap = 'round'
      ctx.stroke()
    }

    ctx.globalAlpha = 1
    animationId = requestAnimationFrame(draw)
  }

  draw()

  // Store cleanup
  onBeforeUnmount(() => {
    if (animationId !== null) {
      cancelAnimationFrame(animationId)
    }
    resizeObserver.disconnect()
  })
}

onMounted(() => {
  startAnimation()
})
</script>
